<template>
  <div class="audio-monitor" :class="{ 'is-compact': compact }">
    <!-- Full mode header -->
    <div v-if="!compact" class="monitor-header">
      <span class="icon"><i class="mdi mdi-speaker"></i></span>
      <span class="monitor-title">Audio Monitor</span>
      <span v-if="!isConnected" class="tag is-warning is-light">Not Connected</span>
      <span v-else-if="!hasAudio" class="tag is-danger is-light">No Audio Signal</span>
      <span v-else class="tag is-success is-light">{{ channelInfo }}</span>
    </div>

    <div class="meters-container">
      <!-- Left Channel -->
      <div class="channel-meter">
        <div class="channel-label">L</div>
        <div class="meter-track">
          <div class="meter-fill" :style="{ height: leftLevel + '%', backgroundColor: getMeterColor(leftLevel) }"></div>
          <div class="meter-peak" :style="{ bottom: leftPeak + '%' }" v-if="leftPeak > 0"></div>
        </div>
        <div v-if="!compact" class="meter-scale">
          <span>0</span>
          <span>-6</span>
          <span>-12</span>
          <span>-24</span>
          <span>-48</span>
        </div>
      </div>

      <!-- Right Channel -->
      <div class="channel-meter">
        <div class="channel-label">R</div>
        <div class="meter-track">
          <div class="meter-fill" :style="{ height: rightLevel + '%', backgroundColor: getMeterColor(rightLevel) }"></div>
          <div class="meter-peak" :style="{ bottom: rightPeak + '%' }" v-if="rightPeak > 0"></div>
        </div>
        <div v-if="!compact" class="meter-scale">
          <span>0</span>
          <span>-6</span>
          <span>-12</span>
          <span>-24</span>
          <span>-48</span>
        </div>
      </div>

      <!-- Channel Status Indicators (full mode only) -->
      <div v-if="!compact" class="channel-status">
        <div class="status-item">
          <span class="status-indicator" :class="{ active: leftActive, warning: leftLevel > 95 }"></span>
          <span class="status-label">Left</span>
        </div>
        <div class="status-item">
          <span class="status-indicator" :class="{ active: rightActive, warning: rightLevel > 95 }"></span>
          <span class="status-label">Right</span>
        </div>
        <div class="status-item stereo-check">
          <span class="icon is-small" :class="isStereo ? 'has-text-success' : 'has-text-warning'">
            <i :class="isStereo ? 'mdi mdi-check-circle' : 'mdi mdi-alert-circle'"></i>
          </span>
          <span class="status-label">{{ isStereo ? 'Stereo' : 'Mono/Missing' }}</span>
        </div>
      </div>
    </div>

    <!-- Compact mode status indicator -->
    <div v-if="compact" class="compact-status">
      <span class="icon is-small" :class="getCompactStatusClass">
        <i :class="getCompactStatusIcon"></i>
      </span>
    </div>

    <!-- dB readings (full mode only) -->
    <div v-if="!compact && isConnected && hasAudio" class="db-readings">
      <span class="db-value">L: {{ leftDb.toFixed(1) }} dB</span>
      <span class="db-value">R: {{ rightDb.toFixed(1) }} dB</span>
    </div>
  </div>
</template>

<script>
// Global registry to track video elements that have MediaElementSource nodes
// This prevents the "already connected" error when createMediaElementSource is called twice
const audioSourceRegistry = new WeakMap()

export default {
  name: 'AudioMonitor',
  props: {
    // Video element to monitor (passed from parent)
    videoElement: {
      type: [HTMLVideoElement, Object],
      default: null
    },
    // Compact vertical mode for sidebar display
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      audioContext: null,
      analyserLeft: null,
      analyserRight: null,
      splitter: null,
      source: null,
      leftLevel: 0,
      rightLevel: 0,
      leftPeak: 0,
      rightPeak: 0,
      leftDb: -48,
      rightDb: -48,
      leftActive: false,
      rightActive: false,
      isConnected: false,
      hasAudio: false,
      animationFrame: null,
      peakHoldTime: 2000, // ms to hold peak
      leftPeakTimeout: null,
      rightPeakTimeout: null,
      channelCount: 0,
      usingExistingSource: false
    }
  },
  computed: {
    isStereo() {
      // Consider stereo if both channels have had signal
      return this.leftActive && this.rightActive
    },
    channelInfo() {
      if (this.channelCount === 0) return 'Detecting...'
      if (this.channelCount === 1) return 'Mono'
      if (this.channelCount === 2) return 'Stereo'
      return `${this.channelCount} Channels`
    },
    getCompactStatusClass() {
      if (!this.isConnected) return 'has-text-warning'
      if (!this.hasAudio) return 'has-text-danger'
      if (this.isStereo) return 'has-text-success'
      return 'has-text-warning'
    },
    getCompactStatusIcon() {
      if (!this.isConnected) return 'mdi mdi-connection'
      if (!this.hasAudio) return 'mdi mdi-volume-off'
      if (this.isStereo) return 'mdi mdi-check-circle'
      return 'mdi mdi-alert-circle'
    }
  },
  watch: {
    videoElement: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.connectToVideo(newVal)
          })
        }
      }
    }
  },
  methods: {
    connectToVideo(video) {
      if (!video) return

      // Clean up existing monitoring (but not the source - it might be reused)
      this.stopMonitoring()

      try {
        // Check if this video element already has a MediaElementSource
        const existingEntry = audioSourceRegistry.get(video)

        if (existingEntry) {
          // Reuse existing audio context and source
          this.audioContext = existingEntry.audioContext
          this.source = existingEntry.source
          this.splitter = existingEntry.splitter
          this.analyserLeft = existingEntry.analyserLeft
          this.analyserRight = existingEntry.analyserRight
          this.usingExistingSource = true
          console.log('Audio monitor: Reusing existing audio source')
        } else {
          // Create new audio context and source
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)()

          // Create media element source - this can only be called ONCE per video element
          this.source = this.audioContext.createMediaElementSource(video)

          // Create channel splitter for stereo analysis
          this.splitter = this.audioContext.createChannelSplitter(2)

          // Create analysers for each channel
          this.analyserLeft = this.audioContext.createAnalyser()
          this.analyserRight = this.audioContext.createAnalyser()

          this.analyserLeft.fftSize = 256
          this.analyserRight.fftSize = 256
          this.analyserLeft.smoothingTimeConstant = 0.8
          this.analyserRight.smoothingTimeConstant = 0.8

          // Connect: source -> splitter -> analysers
          this.source.connect(this.splitter)
          this.splitter.connect(this.analyserLeft, 0) // Left channel
          this.splitter.connect(this.analyserRight, 1) // Right channel

          // Also connect to destination so audio plays
          this.source.connect(this.audioContext.destination)

          // Store in registry for reuse
          audioSourceRegistry.set(video, {
            audioContext: this.audioContext,
            source: this.source,
            splitter: this.splitter,
            analyserLeft: this.analyserLeft,
            analyserRight: this.analyserRight
          })

          this.usingExistingSource = false
          console.log('Audio monitor: Created new audio source')
        }

        // Resume audio context if suspended (required by browsers after user interaction)
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().then(() => {
            console.log('Audio monitor: AudioContext resumed successfully')
          }).catch(err => {
            console.warn('Audio monitor: Failed to resume AudioContext:', err)
          })
        }

        // Log the current audio context state
        console.log('Audio monitor: AudioContext state:', this.audioContext.state)
        console.log('Audio monitor: Video muted:', video.muted, 'Volume:', video.volume)

        // Detect channel count from source
        this.channelCount = 2 // Default to stereo

        this.isConnected = true
        this.startMonitoring()
      } catch (err) {
        console.error('Failed to connect audio monitor:', err)
        // Don't let audio monitor errors affect video playback
        this.isConnected = false
      }
    },

    stopMonitoring() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
        this.animationFrame = null
      }
    },

    disconnect() {
      // Stop monitoring animation
      this.stopMonitoring()

      // Don't disconnect or close if using a shared source from the registry
      // The source needs to stay connected to the destination for audio to play
      if (!this.usingExistingSource && this.source) {
        try {
          this.source.disconnect()
        } catch (e) {
          // Ignore disconnect errors
        }
      }

      // Don't close audio context if it might be reused
      // Closing it would break the audio routing

      // Clear local references but keep registry intact
      this.source = null
      this.audioContext = null
      this.analyserLeft = null
      this.analyserRight = null
      this.splitter = null
      this.isConnected = false
      this.usingExistingSource = false
    },

    startMonitoring() {
      if (!this.analyserLeft || !this.analyserRight) return

      const bufferLengthLeft = this.analyserLeft.frequencyBinCount
      const bufferLengthRight = this.analyserRight.frequencyBinCount
      const dataArrayLeft = new Uint8Array(bufferLengthLeft)
      const dataArrayRight = new Uint8Array(bufferLengthRight)

      const analyze = () => {
        if (!this.analyserLeft || !this.analyserRight) return

        this.analyserLeft.getByteFrequencyData(dataArrayLeft)
        this.analyserRight.getByteFrequencyData(dataArrayRight)

        // Calculate RMS for each channel
        const leftRms = this.calculateRms(dataArrayLeft)
        const rightRms = this.calculateRms(dataArrayRight)

        // Convert to percentage (0-100)
        this.leftLevel = Math.min(100, (leftRms / 255) * 100)
        this.rightLevel = Math.min(100, (rightRms / 255) * 100)

        // Convert to dB (approximate)
        this.leftDb = leftRms > 0 ? 20 * Math.log10(leftRms / 255) : -48
        this.rightDb = rightRms > 0 ? 20 * Math.log10(rightRms / 255) : -48

        // Track peaks
        if (this.leftLevel > this.leftPeak) {
          this.leftPeak = this.leftLevel
          clearTimeout(this.leftPeakTimeout)
          this.leftPeakTimeout = setTimeout(() => {
            this.leftPeak = this.leftLevel
          }, this.peakHoldTime)
        }

        if (this.rightLevel > this.rightPeak) {
          this.rightPeak = this.rightLevel
          clearTimeout(this.rightPeakTimeout)
          this.rightPeakTimeout = setTimeout(() => {
            this.rightPeak = this.rightLevel
          }, this.peakHoldTime)
        }

        // Determine if channels are active (threshold check)
        const activeThreshold = 2 // Minimum level to consider "active"
        this.leftActive = this.leftLevel > activeThreshold
        this.rightActive = this.rightLevel > activeThreshold
        this.hasAudio = this.leftActive || this.rightActive

        this.animationFrame = requestAnimationFrame(analyze)
      }

      analyze()
    },

    calculateRms(dataArray) {
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i]
      }
      return Math.sqrt(sum / dataArray.length)
    },

    getMeterColor(level) {
      if (level > 95) return '#ff3860' // Red - clipping
      if (level > 80) return '#ffdd57' // Yellow - hot
      if (level > 20) return '#23d160' // Green - good
      return '#48c774' // Light green - low
    }
  },
  beforeDestroy() {
    // Stop monitoring but don't fully disconnect - audio needs to keep playing
    this.stopMonitoring()
    clearTimeout(this.leftPeakTimeout)
    clearTimeout(this.rightPeakTimeout)
  }
}
</script>

<style scoped>
.audio-monitor {
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 8px;
  padding: 16px;
  color: #fff;
}

.monitor-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.monitor-title {
  font-weight: 600;
  flex: 1;
}

.meters-container {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.channel-meter {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.channel-label {
  font-weight: bold;
  font-size: 14px;
  color: #fff;
}

.meter-track {
  width: 30px;
  height: 150px;
  background: #0a0a1a;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.meter-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transition: height 0.05s ease-out;
  border-radius: 0 0 3px 3px;
}

.meter-peak {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: #ff3860;
  transition: bottom 0.05s ease-out;
}

.meter-scale {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 150px;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  margin-left: 4px;
}

.channel-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #363636;
  border: 2px solid #484848;
  transition: all 0.1s ease;
}

.status-indicator.active {
  background: #23d160;
  border-color: #1a9e4a;
  box-shadow: 0 0 8px rgba(35, 209, 96, 0.5);
}

.status-indicator.warning {
  background: #ff3860;
  border-color: #cc2e4d;
  box-shadow: 0 0 8px rgba(255, 56, 96, 0.5);
}

.status-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.stereo-check {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.db-readings {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.db-value {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #48c774;
}

/* Compact mode styles - vertical sidebar layout */
.audio-monitor.is-compact {
  padding: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.audio-monitor.is-compact .meters-container {
  flex: 1;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
  align-items: stretch;
}

.audio-monitor.is-compact .channel-meter {
  flex: 1;
  max-width: 28px;
}

.audio-monitor.is-compact .channel-label {
  font-size: 11px;
  margin-bottom: 4px;
}

.audio-monitor.is-compact .meter-track {
  width: 100%;
  height: 100%;
  min-height: 200px;
  flex: 1;
}

.audio-monitor.is-compact .compact-status {
  display: flex;
  justify-content: center;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.audio-monitor.is-compact .compact-status .icon {
  font-size: 16px;
}
</style>
